/**
 * Settings Page
 * Modern settings page with sidebar navigation and organized sections
 * Follows design patterns from Linear, Stripe, and Vercel
 */

import { PageHeader } from '@/components/layout'
import { SettingsLayout, SettingsSectionContent } from '@/components/settings/SettingsLayout'
import {
  AccountSection,
  AdminSection,
  AppearanceSection,
  DataQualitySection,
  FeedbackSection,
  useIsAdmin,
} from '@/components/settings/sections'
import { CreditCard, MessageSquarePlus, Palette, Shield, ShieldAlert } from 'lucide-react'

const SETTINGS_SECTIONS = [
  {
    id: 'account',
    label: 'Account',
    icon: CreditCard,
    description: 'View your profile, subscription tier, and usage statistics',
  },
  {
    id: 'data-quality',
    label: 'Data Quality',
    icon: Shield,
    description: 'Configure data validation and quality control settings',
  },
  {
    id: 'appearance',
    label: 'Appearance',
    icon: Palette,
    description: 'Customize metric colors and visual preferences',
  },
  {
    id: 'feedback',
    label: 'Feedback',
    icon: MessageSquarePlus,
    description: 'Submit feedback and access help resources',
  },
]

const ADMIN_SECTION = {
  id: 'admin',
  label: 'Admin',
  icon: ShieldAlert,
  description: 'Development tools for testing subscription tiers',
}

export function Settings() {
  const isAdmin = useIsAdmin()

  // Add admin section if user is admin
  const sections = isAdmin
    ? [...SETTINGS_SECTIONS, ADMIN_SECTION]
    : SETTINGS_SECTIONS

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <PageHeader
        title="Settings"
        description="Manage your account and configure your optimizer experience"
      />

      <SettingsLayout sections={sections} defaultSection="account">
        <SettingsSectionContent
          id="account"
          title="Account"
          description="View your profile, subscription tier, and usage statistics"
        >
          <AccountSection />
        </SettingsSectionContent>

        <SettingsSectionContent
          id="data-quality"
          title="Data Quality"
          description="Configure data validation and quality control settings"
        >
          <DataQualitySection />
        </SettingsSectionContent>

        <SettingsSectionContent
          id="appearance"
          title="Appearance"
          description="Customize metric colors and visual preferences"
        >
          <AppearanceSection />
        </SettingsSectionContent>

        <SettingsSectionContent
          id="feedback"
          title="Feedback"
          description="Submit feedback and access help resources"
        >
          <FeedbackSection />
        </SettingsSectionContent>

        {isAdmin && (
          <SettingsSectionContent
            id="admin"
            title="Admin Controls"
            description="Development tools for testing subscription tiers"
          >
            <AdminSection />
          </SettingsSectionContent>
        )}
      </SettingsLayout>
    </div>
  )
}
